/**
 * Строки, разделяющие сессии приложений на одном origin: localStorage, sessionStorage, BroadcastChannel и Web Locks у
 * Front и админки общие, а API не проверяют `aud` — изоляция на клиенте держится только на этих строках.
 * Умолчания нет: фабрики требуют namespace явно. Менять строки нельзя без миграции ключей (иначе массовый выход и
 * брошенные refresh-токены в localStorage), CSRF — только вместе с сервером (`CsrfTokenDescriptor`), строки Front —
 * ещё и из-за FrontVTB, который читает те же ключи токенов.
 */
export interface AuthNamespace {
    /** localStorage, body-режим */
    readonly accessTokenKey: string;
    readonly refreshTokenKey: string;
    /** localStorage: последний фактический режим сервера — до ответа SignInSettings */
    readonly authModeKey: string;
    /** localStorage: корреляционный id устройства; при выходе не чистится */
    readonly deviceIdKey: string;
    /** sessionStorage: returnUrl на время входа через IdP */
    readonly samlReturnUrlKey: string;
    /** BroadcastChannel; ключ запасной доставки через storage — `authPingKey(namespace)` */
    readonly broadcastChannel: string;
    /** Web Lock ротации refresh */
    readonly refreshLockName: string;
    /** double-submit CSRF: имена cookie и заголовка — как в `CsrfTokenDescriptor` сервиса */
    readonly csrfCookie: string;
    readonly csrfHeader: string;
}

export const FRONT_AUTH_NAMESPACE: AuthNamespace = Object.freeze({
    accessTokenKey: 'frontAccessToken',
    refreshTokenKey: 'frontRefreshToken',
    authModeKey: 'frontAuthMode',
    deviceIdKey: 'frontDeviceId',
    samlReturnUrlKey: 'frontSamlReturnUrl',
    broadcastChannel: 'frontend-auth',
    refreshLockName: 'frontend-auth-refresh',
    csrfCookie: 'XSRF-TOKEN',
    csrfHeader: 'X-XSRF-TOKEN',
});

export const CONTROL_PANEL_AUTH_NAMESPACE: AuthNamespace = Object.freeze({
    accessTokenKey: 'controlPanelAccessToken',
    refreshTokenKey: 'controlPanelRefreshToken',
    authModeKey: 'controlPanelAuthMode',
    deviceIdKey: 'controlPanelDeviceId',
    samlReturnUrlKey: 'controlPanelSamlReturnUrl',
    broadcastChannel: 'controlpanel-auth',
    refreshLockName: 'controlpanel-auth-refresh',
    // Отличаются от Front: оба SPA на одном домене с Path=/, одинаковые имена перетёрли бы cookie друг друга.
    csrfCookie: 'XSRF-CP-TOKEN',
    csrfHeader: 'X-XSRF-CP-TOKEN',
});

/** Ключ localStorage запасной доставки событий канала (браузер без BroadcastChannel). */
export const authPingKey = (namespace: Pick<AuthNamespace, 'broadcastChannel'>): string =>
    `${namespace.broadcastChannel}__ping__`;

/** Все строки namespace вместе с производным ключом запасной доставки — для реестра и проверки непересечения. */
export function authNamespaceStrings(namespace: AuthNamespace): string[] {
    return [
        namespace.accessTokenKey,
        namespace.refreshTokenKey,
        namespace.authModeKey,
        namespace.deviceIdKey,
        namespace.samlReturnUrlKey,
        namespace.broadcastChannel,
        authPingKey(namespace),
        namespace.refreshLockName,
        namespace.csrfCookie,
        namespace.csrfHeader,
    ];
}
